/**
 * chartMind7PromptRegistry.js — specialty, setting, and combination prompt ids.
 * Shaped by: CM7-02; decisions D-CM7-15, D-CM7-16, D-CM7-17, D-CM7-18, D-CM7-02-01.
 * Invariants: separator is "--" (no existing parser splits prompt ids on it).
 *   Allow only the six base ids × enabled specialty keys × outpatient|inpatient.
 *   No Firebase imports. Keep the allowlist identical to functions/src/chartMind7/comboPromptIds.js.
 * Related: acgmeSpecialties.js, firebase/seed/chartMind7.promptAddenda.json.
 */

import { ACGME_SPECIALTIES } from './acgmeSpecialties';

/** D-CM7-02-01. Base prompt ids in this repo do not contain this separator. */
export const CM7_SEPARATOR = '--';

export const CM7_SETTINGS = Object.freeze(['outpatient', 'inpatient']);

/** Mode → existing shared base prompt id (D-CM7-15). No chartmind7-* bases. */
export const CM7_MODES = Object.freeze({
  differential: 'chartmind-diagnosis',
  diagnostics: 'chartmind-diagnostic-tests-scmap4',
  treatment: 'chartmind-treatment',
  discharge: 'chartmind-discharge',
  documentation: 'chartmind-notes',
  pearls: 'dp-quick-draft-pearls',
});

export const CM7_DEFAULT_SELECTION = Object.freeze({
  specialtyKey: 'em',
  setting: 'outpatient',
});

const BASE_IDS = new Set(Object.values(CM7_MODES));
const SETTINGS = new Set(CM7_SETTINGS);

export function listEnabledSpecialties() {
  return ACGME_SPECIALTIES.filter((specialty) => specialty.enabled);
}

/**
 * @param {string} baseId
 * @param {string} specialtyKey
 * @param {string} setting
 * @returns {string}
 */
export function buildComboPromptId(baseId, specialtyKey, setting) {
  return `${baseId}${CM7_SEPARATOR}${specialtyKey}${CM7_SEPARATOR}${setting}`;
}

/**
 * @param {unknown} id
 * @returns {{ baseId: string, specialtyKey: string, setting: string } | null}
 */
export function parseComboPromptId(id) {
  if (typeof id !== 'string' || id.length === 0) return null;
  const parts = id.split(CM7_SEPARATOR);
  if (parts.length !== 3) return null;
  const [baseId, specialtyKey, setting] = parts;
  if (!baseId || !specialtyKey || !setting) return null;
  return { baseId, specialtyKey, setting };
}

/**
 * True only for a known base id, an enabled specialty key, and a known setting.
 * @param {unknown} id
 * @returns {boolean}
 */
export function isAllowedComboPromptId(id) {
  const parsed = parseComboPromptId(id);
  if (!parsed) return false;
  if (!BASE_IDS.has(parsed.baseId)) return false;
  if (!SETTINGS.has(parsed.setting)) return false;
  const specialty = ACGME_SPECIALTIES.find((entry) => entry.key === parsed.specialtyKey);
  return Boolean(specialty && specialty.enabled);
}
